import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import { LogOut } from "lucide-react";

const NAV_ITEMS = [{ label: "候補者管理", to: "/candidates" }] as const;

const MARKETING_NAV_ITEMS = [
  { href: "#problem", label: "課題" },
  { href: "#features", label: "特徴" },
  { href: "#flow", label: "面談の流れ" },
  { href: "#faq", label: "FAQ" },
] as const;

interface HeaderProps {
  mode?: "operator" | "public" | "marketing";
  userName?: string;
  userEmail?: string;
  onLogout?: () => void;
}

function isNavActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function Brand() {
  return (
    <div className="flex shrink-0 items-center gap-2.5" aria-label="Relance AI面談">
      <img
        src="/Relance_logo.svg"
        alt="Relance"
        width={247}
        height={71}
        className="h-auto w-20 sm:w-28"
      />
      <span className="h-5 w-px bg-primary-100" aria-hidden="true" />
      <span className="whitespace-nowrap text-xs font-bold text-[#152C4D] sm:text-sm">AI面談</span>
    </div>
  );
}

function MarketingBrand() {
  return (
    <div className="flex shrink-0 items-center gap-2.5" aria-label="Relance AI面談 Beta">
      <img src="/Relance_logo.svg" alt="Relance" width={247} height={71} className="h-10 w-auto" />
      <span className="whitespace-nowrap text-[22px] font-bold leading-[1.7] tracking-[0.32px] text-black">
        AI面談
      </span>
      <span className="rounded-full bg-[#ecf6f5] px-4 text-[13px] leading-[22px] tracking-[0.32px] text-[#00666b]">
        Beta
      </span>
    </div>
  );
}

function OperatorNavigation() {
  const { location } = useRouterState();
  const pathname = location.pathname;

  return (
    <nav className="hidden h-16 items-center gap-6 md:flex" aria-label="管理画面">
      {NAV_ITEMS.map((item) => {
        const active = isNavActive(pathname, item.to);
        return (
          <Link
            key={item.label}
            to={item.to}
            className={cn(
              "flex h-16 items-center border-b-2 px-1 text-sm font-medium transition-colors",
              active
                ? "border-primary font-bold text-primary"
                : "border-transparent text-muted-foreground hover:text-primary"
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function MarketingNavigation({
  label,
  onNavigate,
}: {
  label: string;
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label={label}>
      {MARKETING_NAV_ITEMS.map((item) => (
        <a
          key={item.href}
          href={item.href}
          className="text-sm font-medium text-[#152C4D] transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-4"
          onClick={onNavigate}
        >
          {item.label}
        </a>
      ))}
    </nav>
  );
}

export function Header({ mode = "operator", onLogout, userEmail, userName }: HeaderProps) {
  const isOperator = mode === "operator";
  const isMarketing = mode === "marketing";

  return (
    <header
      data-testid="app-header"
      className={cn(
        "sticky top-0 z-50 bg-white",
        isMarketing
          ? "border-0 shadow-none"
          : "border-b border-primary-100 bg-white/95 shadow-sm backdrop-blur"
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-between gap-3",
          isMarketing
            ? "h-[76px] px-5 lg:h-[78px] lg:px-10 xl:px-[110px]"
            : "h-16 px-4 sm:px-6 md:px-10"
        )}
      >
        <div
          className={cn(
            "flex min-w-0 items-center",
            isMarketing ? "gap-12 xl:gap-[150px]" : "gap-6 lg:gap-8"
          )}
        >
          {isMarketing ? <MarketingBrand /> : <Brand />}
          {isOperator && <OperatorNavigation />}
          {isMarketing && (
            <div className="hidden lg:block [&_nav]:flex [&_nav]:items-center [&_nav]:gap-6">
              <MarketingNavigation label="ランディングページ" />
            </div>
          )}
        </div>

        {isOperator && (
          <div className="flex shrink-0 items-center gap-2 sm:gap-3 lg:gap-4">
            {userEmail && (
              <div className="hidden text-right lg:block">
                <p className="text-sm font-semibold text-slate-900">{userName ?? userEmail}</p>
                <p className="text-xs text-slate-500">{userEmail}</p>
              </div>
            )}
            <span className="hidden rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary xl:inline-flex">
              管理画面
            </span>
            <Button variant="ghost" size="sm" onClick={onLogout} aria-label="ログアウト">
              <LogOut className="size-4" />
              <span className="hidden sm:inline">ログアウト</span>
            </Button>
          </div>
        )}

        {isMarketing && (
          <a
            href="https://ai.relance.jp/"
            className="hidden h-[42px] shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-[#008c99] to-[#2dbf9b] px-6 text-sm font-bold text-white transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#008c99] focus-visible:ring-offset-2 lg:inline-flex"
          >
            AI面談を受ける
          </a>
        )}
      </div>
    </header>
  );
}
